import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Alert from '@pelckmans/elementis/components/alert';
import Button from '@pelckmans/elementis/components/button';
import Dialog from '@pelckmans/elementis/components/dialog';
import SearchField from '@pelckmans/elementis/components/search-field';

import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../../constants/analyticsEvents';
import ModalContext from '../../../../../../contexts/ModalContext';
import useAnalytics from '../../../../../../hooks/useAnalytics';
import LoadingState from '../../../../../../shared-components/LoadingState';
import ModuleSelectionField from '../../../../../../shared-components/ModuleSelectionField';
import NoContent from '../../../../../../shared-components/NoContent';
import ModuleContext from '../../../../_contexts/ModuleContext';
import TableOfContentsContext from '../../../_contexts/TableOfContentsContext';
import { copyMaterial, ModuleContainingUserMaterial } from './api';
import { SelectionState } from './components/NodeBranch';
import NodeTree from './components/NodeTree';
import useModulesWithUserMaterial, { SubModule } from './hooks/useModulesWithUserMaterial';
import { updateNodeTreeSelection } from './util/selection';

type Props = {
  id: string;
  onSubmit: () => void;
};

const BASE = 'PAGES.TOC_LEVEL.MODALS.COPY_MATERIAL';

export default function CopyMaterialModal({ id, onSubmit: onSubmitCallback }: Props) {
  const { t } = useTranslation();
  const { closeModal } = useContext(ModalContext);
  const { selectedNode } = useContext(TableOfContentsContext);
  const { module: currentModule } = useContext(ModuleContext);

  const [selection, setSelection] = useState<Record<string, SelectionState>>({});
  const [showAlert, setShowAlert] = useState<'no-selection' | 'retry' | null>(null);
  const [selectedSuperModule, setSelectedSuperModule] = useState<ModuleContainingUserMaterial | null>(null);
  const [selectedSubModule, setSelectedSubModule] = useState<SubModule | null>(null);

  const { modules, nodesWithMaterial, allNodesWithMaterialById, isLoading } =
    useModulesWithUserMaterial(selectedSubModule);

  const { capture } = useAnalytics();

  useEffect(() => {
    if (!modules.length) return;
    if (!selectedSuperModule) {
      setSelectedSuperModule(modules[0]);
      setSelectedSubModule(modules[0].books[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modules]);

  useEffect(() => {
    if (selectedSubModule) {
      setSelection({});
    }
  }, [selectedSubModule]);

  function onSelectionChange(nodeId: string, rowId: string) {
    const newSelection = updateNodeTreeSelection(nodeId, rowId, selection, allNodesWithMaterialById);
    setSelection(newSelection);
  }

  function handleClose() {
    closeModal(id, true);
  }

  async function handleConfirmClick() {
    if (!currentModule || !selectedNode) return;

    const materialToCopy = Object.keys(selection).filter(key => !allNodesWithMaterialById[key]);
    if (materialToCopy.length) {
      try {
        await copyMaterial(currentModule.id, selectedNode.id, materialToCopy);

        onSubmitCallback();

        const subLocationId = currentModule?.isExerciseModule
          ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_MATERIAL
          : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL;

        capture({
          objectId: id,
          eventType: ANALYTICS_EVENT_TYPES.USER_MATERIAL_COPIED,
          subLocationId,
        });

        handleClose();
      } catch (error) {
        setShowAlert('retry');
      }
    } else {
      setShowAlert('no-selection');
    }
  }

  function renderAlert() {
    if (showAlert === 'no-selection') {
      return <Alert prominence="warning">{t(`${BASE}.ALERTS.NO_SELECTION`)}</Alert>;
    }
    if (showAlert === 'retry') {
      return (
        <Alert
          prominence="danger"
          actions={{
            'aria-label': undefined,
            role: undefined,
            children: (
              <Button prominence="danger" onClick={handleConfirmClick}>
                {t(`${BASE}.ALERTS.RETRY.ACTION`)}
              </Button>
            ),
          }}
        >
          {t(`${BASE}.ALERTS.RETRY.TEXT`)}
        </Alert>
      );
    }

    return null;
  }

  function renderBody() {
    if (isLoading) {
      return <LoadingState variant="list" aria-label={t(`${BASE}.LOADING.A11Y_LABEL`)} />;
    }

    if (nodesWithMaterial.length === 0 || !selectedSuperModule) {
      return <NoContent title={t(`${BASE}.EMPTY.TITLE`)}>{t(`${BASE}.EMPTY.TEXT`)}</NoContent>;
    }

    return (
      <NodeTree
        nodes={nodesWithMaterial}
        onSelectionChange={onSelectionChange}
        selection={selection}
        selectedSuperModule={selectedSuperModule}
      />
    );
  }

  return (
    <Dialog
      id={id}
      modal
      className="copy-material-modal"
      onClose={handleClose}
      size="sm"
      aria-label={t(`${BASE}.A11Y_LABEL`)}
    >
      <Dialog.Header title={t(`${BASE}.TITLE`)} onClose={handleClose} aria-label={t('ACTIONS.CLOSE')}>
        <div className="header-controls-wrapper">
          <SearchField
            placeholder={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.SEARCH_FIELD.PLACEHOLDER')}
            clearActionLabel={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.SEARCH_FIELD.CLEAR_A11Y_LABEL')}
            searchActionLabel={t('PAGES.RESULTS.MODALS.SCORABLE_ITEMS_FILTER.SEARCH_FIELD.BUTTON_TEXT')}
            onSearch={() => {}}
            onClear={() => {}}
          />
          {modules.length ? (
            <div className="header-controls-wrapper__module_filters">
              <ModuleSelectionField
                key="super-module-filter"
                className="header-controls-wrapper__module_filters__super-module-filter"
                modules={modules}
                selectedModule={selectedSuperModule}
                setSelectedModule={module => {
                  setSelectedSuperModule(module);
                  setSelectedSubModule(module.books[0]);
                }}
                ariaLabel={t(`${BASE}.MODULE_FILTER.A11Y_LABEL`)}
                dropdownMenuAriaLabel={t(`${BASE}.MODULE_FILTER.DROPDOWN_MENU.A11Y_LABEL`)}
                useCatalogName
                size="md"
              />
              <ModuleSelectionField
                key="sub-module-filter"
                className="header-controls-wrapper__module_filters__sub-module-filter"
                modules={selectedSuperModule?.books || []}
                selectedModule={selectedSubModule}
                setSelectedModule={module => {
                  setSelectedSubModule(module);
                }}
                ariaLabel={t(`${BASE}.SUB_MODULE_FILTER.A11Y_LABEL`)}
                dropdownMenuAriaLabel={t(`${BASE}.SUB_MODULE_FILTER.DROPDOWN_MENU.A11Y_LABEL`)}
                size="md"
              />
            </div>
          ) : undefined}
        </div>
      </Dialog.Header>
      <Dialog.Body
        alertGroupProps={showAlert ? { 'aria-label': undefined, role: undefined, children: renderAlert()! } : undefined}
      >
        {renderBody()}
      </Dialog.Body>
      <Dialog.Footer
        buttonGroupPropsRight={{
          role: 'group',
          'aria-label': undefined,
          children: (
            <>
              <Button prominence="neutral" onClick={handleClose} key="cancel">
                {t('ACTIONS.CANCEL')}
              </Button>
              <Button prominence="success" onClick={handleConfirmClick} key="apply">
                {t(`${BASE}.CONFIRM`)}
              </Button>
            </>
          ),
        }}
      />
    </Dialog>
  );
}
